import React, { useState, useEffect, useMemo } from 'react';

interface ImageGeneratorProps {
  onGenerate: (prompt: string) => Promise<void>;
  generatedImage: string | null;
  isLoading: boolean;
  error: string | null;
}

interface Tag {
  id: string;
  text: string;
}

// Input validation and sanitization functions
const sanitizeInput = (input: string): string => {
  // Remove any HTML tags and dangerous characters while preserving spaces and safe characters
  const sanitized = input
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[\\{}]/g, '') // Remove only dangerous characters
    .replace(/[;`]/g, ''); // Remove dangerous punctuation
  return sanitized; // Don't trim to preserve spaces
};

const isValidInput = (input: string): boolean => {
  // Check for maximum length
  if (input.length > 200) return false;
  if (input.length === 0) return true; // Allow empty input while typing
  
  // Allow letters, numbers, spaces, brackets, and common punctuation/symbols
  const validPattern = /^[ a-zA-Z0-9.,!?&()[\]\-_'"/]+$/;
  return validPattern.test(input);
};

const ImageGenerator = ({
  onGenerate,
  generatedImage,
  isLoading,
  error,
}: ImageGeneratorProps) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [defaultTags, setDefaultTags] = useState<Tag[]>([
    { id: '1', text: 'photo realistic' },
    { id: '2', text: 'clay coffee cup' },
    { id: '3', text: 'high quality' }
  ]);
  const [defaultInputValue, setDefaultInputValue] = useState('');
  
  const defaultPrompt = useMemo(() => {
    return `Generate an image with ${defaultTags.map(tag => tag.text).join(', ')}`;
  }, [defaultTags]);

  // Calculate button disabled state whenever relevant states change
  useEffect(() => {
    const shouldBeDisabled = isLoading || defaultTags.length === 0;
    setIsButtonDisabled(shouldBeDisabled);
  }, [isLoading, defaultTags]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onGenerate(defaultPrompt);
  };

  const handleReset = () => {
    setDefaultTags([
      { id: '1', text: 'photo realistic' },
      { id: '2', text: 'clay coffee cup' },
      { id: '3', text: 'high quality' }
    ]);
    setDefaultInputValue('');
    // Clear the generated image by calling onGenerate with an empty string
    onGenerate('');
  };

  const handleClear = () => {
    setDefaultTags([]); // Clear all tags
    setDefaultInputValue('');
    // Clear the generated image by calling onGenerate with an empty string
    onGenerate('');
  };

  const handleDefaultInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ',' && defaultInputValue.trim()) {
      e.preventDefault();
      const sanitizedValue = sanitizeInput(defaultInputValue.trim());
      
      // Only create tag if input is valid
      if (isValidInput(sanitizedValue)) {
        const newTag = {
          id: Date.now().toString(),
          text: sanitizedValue
        };
        setDefaultTags(prev => [...prev, newTag]);
        setDefaultInputValue('');
      } else {
        // Show error message for invalid input
        const errorDiv = document.createElement('p');
        errorDiv.className = 'mt-1 text-sm text-red-600 animate-fade-out';
        errorDiv.textContent = 'Input contains invalid characters';
        const inputElement = e.currentTarget.parentElement;
        inputElement?.appendChild(errorDiv);
        
        // Remove error message after 3 seconds
        setTimeout(() => {
          errorDiv.remove();
        }, 3000);
      }
    }
  };

  const handleDefaultInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value.includes(',')) {
      const sanitizedValue = sanitizeInput(value);
      setDefaultInputValue(sanitizedValue);
    }
  };

  const removeTag = (tagId: string) => {
    setDefaultTags(prev => prev.filter(tag => tag.id !== tagId));
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Generate Your Image</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <div>
                <label htmlFor="defaultInput" className="block text-sm font-medium text-gray-700 mb-1">
                  Add tags (press comma to create tag)
                </label>
                <input
                  type="text"
                  id="defaultInput"
                  value={defaultInputValue}
                  onChange={handleDefaultInputChange}
                  onKeyDown={handleDefaultInputKeyDown}
                  placeholder="Type and press comma to add tags"
                  className="input w-full"
                  maxLength={200}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                {defaultTags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
                  >
                    {tag.text}
                    <button
                      type="button"
                      onClick={() => removeTag(tag.id)}
                      className="ml-2 text-primary-600 hover:text-primary-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              {error && (
                <div className="text-red-600 text-sm mt-2">
                  {error}
                </div>
              )}

              {defaultTags.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Current Prompt:</p>
                  <p className="text-sm font-medium text-gray-900">{defaultPrompt}</p>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={isButtonDisabled}
                  className={`flex-1 py-2 px-4 rounded-md text-white font-medium ${
                    isButtonDisabled
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700'
                  }`}
                >
                  {isLoading ? 'Generating...' : 'Generate Your Image'}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="py-2 px-4 rounded-md text-gray-700 font-medium bg-gray-100 hover:bg-gray-200"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleClear}
                  className="py-2 px-4 rounded-md text-red-700 font-medium bg-red-50 hover:bg-red-100"
                >
                  Clear
                </button>
              </div>
            </div>
          </form>

          {generatedImage && (
            <div className="mt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Generated Image</h3>
              <div className="relative aspect-square w-full max-w-lg mx-auto">
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = generatedImage;
                    link.download = 'generated-image.png';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="absolute top-2 right-2 p-2 rounded-full bg-white/90 shadow-md hover:bg-white transition-colors duration-200"
                  title="Download image"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                <img
                  src={generatedImage}
                  alt="Generated image"
                  className="rounded-lg shadow-md w-full h-full object-contain"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageGenerator; 